import type { Folder, Workflow } from "@/types/resources";
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { SortableData } from "@dnd-kit/sortable";
import { arrayMove } from "@dnd-kit/sortable";
import { useEffect, useState } from "react";

export const UNASSIGNED_FOLDER_ID = "UNASSIGNED_FOLDER_ID" as const;
export const ROOT_CONTAINER_ID = "ROOT_CONTAINER_ID" as const;

type DragEndArgs =
  | {
      kind: "folder";
      id: string;
      index: number;
    }
  | {
      kind: "workflow";
      id: string;
      containerId: string;
      index: number;
    };

type Props = {
  folders: Folder[];
  unassignedWorkflows: Workflow[];
  onDragEnd: (args: DragEndArgs) => Promise<unknown>;
};

export const useWorkflowDnD = ({
  folders,
  unassignedWorkflows,
  onDragEnd,
}: Props) => {
  // フォルダー同士の並び順を管理する状態
  const [folderIds, setFoldersIds] = useState(() => initFolderIds(folders));
  // フォルダーごとのワークフローの並び順を管理する状態
  const [workflows, setWorkflows] = useState(() =>
    initWorkflows(folders, unassignedWorkflows)
  );

  useEffect(() => {
    setFoldersIds(initFolderIds(folders));
    setWorkflows(initWorkflows(folders, unassignedWorkflows));
  }, [folders, unassignedWorkflows]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 180,
      tolerance: 10,
    },
  });
  const sensors = useSensors(mouseSensor);

  type ActiveInfo = {
    id: string;
    containerId: string;
    // コンテナを跨いだ移動かどうか
    isCrossOver: boolean;
    // エラーが起きた際に移動前の状態に戻すために保持する
    prevState: {
      folderIds: string[];
      workflows: WorkflowState;
    };
  };
  const [activeInfo, setActiveInfo] = useState<ActiveInfo | null>(null);

  const activeWorkflow = ((currentWorkflows: WorkflowState) => {
    if (activeInfo === null) return null;
    const { id, containerId } = activeInfo;

    if (containerId === ROOT_CONTAINER_ID) {
      const folder = currentWorkflows[id];
      if (folder === undefined) return null;
      return {
        kind: "folder",
        folder,
      } as const;
    }

    const folder = currentWorkflows[containerId];
    if (folder === undefined) return null;

    const workflow = folder.workflows.find((workflow) => workflow.id === id);
    if (workflow === undefined) return null;

    return {
      kind: "workflow",
      workflow,
    } as const;
  })(workflows);

  const handleDragStart = ({ active }: DragStartEvent) => {
    if (!isSoartable(active.data.current)) return;
    setActiveInfo({
      id: active.id.toString(),
      containerId: active.data.current.sortable.containerId.toString(),
      isCrossOver: false,
      prevState: {
        folderIds,
        workflows,
      },
    });
  };

  /**
   * コンテナ（SortableContext）を跨いだ場合に、移動先のコンテナにドラッグ中のアイテムを移動させる
   * フォルダーの移動はコンテナを跨がないので、ここではフォルダーの移動は行わない
   */
  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (activeInfo === null) return;
    if (over === null) return;
    if (active.id === over.id) return;

    const { id, containerId } = activeInfo;
    const activeContainer = isSoartable(active.data.current)
      ? active.data.current.sortable
      : {
          containerId,
          items: [],
          index:
            workflows[containerId]?.workflows.findIndex((w) => w.id === id) ??
            -1,
        };
    const overContainer = isSoartable(over.data.current)
      ? over.data.current.sortable
      : { containerId: over.id, items: [], index: -1 };

    if (activeContainer.containerId === overContainer.containerId) return;
    if (workflows[overContainer.containerId] === undefined) return;
    // ルートコンテナではコンテナ間移動はなく、コンテナ内の移動のみ（つまりフォルダとフォルダの移動）を許可するので処理は handleDragEnd に任せる
    if (activeContainer.containerId === ROOT_CONTAINER_ID) return;

    const activeWorkflow = workflows[
      activeContainer.containerId
    ]?.workflows.find((w) => w.id === active.id);
    if (!activeWorkflow) return;

    setWorkflows((prev) => {
      const active_ = prev[activeContainer.containerId];
      const over_ = prev[overContainer.containerId];
      if (active_ === undefined || over_ === undefined) return prev;
      return {
        ...prev,
        // 移動元のコンテナから active のアイテムを削除
        [activeContainer.containerId]: {
          ...active_,
          workflows: active_.workflows.filter((w) => w.id !== active.id),
        },
        // 移動先のコンテナに active のアイテムを追加
        [overContainer.containerId]: {
          ...over_,
          workflows: [...over_.workflows, activeWorkflow],
        },
      };
    });

    setActiveInfo((prev) => {
      if (prev === null) return null;
      return {
        ...prev,
        isCrossOver: true,
        id: active.id.toString(),
        containerId: overContainer.containerId.toString(),
      };
    });
  };

  /**
   * コンテナ内の移動の fix を行う。
   * handleDragOver がコンテナ間の移動を制御するが、あくまでコンテナの末尾に移動させるだけなので
   * こちらでコンテナ内の最終的な位置を決定する必要がある。
   */
  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (activeInfo === null) return;
    const { prevState, isCrossOver, id, containerId } = activeInfo;
    setActiveInfo(null);

    try {
      if (over === null) return;
      // 移動していない判定の場合は早期リターンしたいが、
      // handleDragOver でコンテナ間移動し、末尾に追加されたそのままの状態でこの処理に来た場合に
      // active と over が同じになるのでその場合は、後続のサーバー通信を行いたいので処理を続行する
      if (active.id === over.id && !isCrossOver) return;

      const activeContainer = isSoartable(active.data.current)
        ? active.data.current.sortable
        : {
            containerId,
            items: [],
            index:
              workflows[containerId]?.workflows.findIndex((w) => w.id === id) ??
              -1,
          };

      if (activeContainer.index === -1) return;

      // フォルダの移動
      if (activeContainer.containerId === ROOT_CONTAINER_ID) {
        // フォルダに属さないワークフローコンテナとの移動は行わない
        if (over.id === UNASSIGNED_FOLDER_ID) return;

        const activeIndex = activeContainer.index;
        const overIndex = folderIds.indexOf(over.id.toString());
        if (overIndex === -1) return;

        setFoldersIds((prev) => arrayMove(prev, activeIndex, overIndex));

        await onDragEnd({
          kind: "folder",
          id: active.id.toString(),
          index: overIndex,
        });
        return;
      }

      // コンテナに移動した時に、衝突検知でコンテナが検出された場合は、 sortable でなくなるので、 active で上書きする
      const overContainer = isSoartable(over.data.current)
        ? over.data.current.sortable
        : activeContainer;

      // 同じコンテナ内での移動
      if (activeContainer.containerId === overContainer.containerId) {
        const activeIndex = activeContainer.index;
        const overIndex = overContainer.index;
        const activeFolder = workflows[activeContainer.containerId];
        if (activeFolder === undefined) return;
        const next = arrayMove(activeFolder.workflows, activeIndex, overIndex);
        setWorkflows((prev) => {
          const active_ = prev[activeContainer.containerId];
          if (active_ === undefined) return prev;
          return {
            ...prev,
            [activeContainer.containerId]: {
              ...active_,
              workflows: next,
            },
          };
        });

        await onDragEnd({
          kind: "workflow",
          id: active.id.toString(),
          containerId: activeContainer.containerId.toString(),
          index: overIndex,
        });
        return;
      }
    } catch (_) {
      // エラーが起きた場合は移動前の状態に戻す
      setFoldersIds(prevState.folderIds);
      setWorkflows(prevState.workflows);
      // TODO: エラーがユーザーに伝わるようにトーストなどを表示するのが好ましい
    }
  };

  return {
    dndProps: {
      sensors,
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragEnd: handleDragEnd,
    },
    activeWorkflow,
    workflows,
    folderIds,
  };
};

function isSoartable(sortable: unknown): sortable is SortableData {
  return (
    sortable !== null &&
    typeof sortable === "object" &&
    "sortable" in sortable &&
    sortable.sortable !== null &&
    typeof sortable.sortable === "object" &&
    "items" in sortable.sortable &&
    "index" in sortable.sortable &&
    "containerId" in sortable.sortable
  );
}

function initFolderIds(folders: Folder[]) {
  return [...folders.map((f) => f.id)];
}

type WorkflowState = Record<
  string,
  { id: string; name: string; workflows: Workflow[] }
>;
function initWorkflows(
  folders: Folder[],
  unassignedWorkflows: Workflow[]
): WorkflowState {
  return {
    [UNASSIGNED_FOLDER_ID]: {
      id: UNASSIGNED_FOLDER_ID,
      name: "",
      workflows: unassignedWorkflows,
    },
    ...folders.reduce<WorkflowState>((acc, folder) => {
      acc[folder.id] = {
        id: folder.id,
        name: folder.name,
        workflows: folder.workflows,
      };
      return acc;
    }, {}),
  };
}
